import auroraTexture from '../../assets/vfx-aurora.webp';
import auroraArcTexture from '../../assets/vfx-aurora-arc.webp';
import auroraReflectionTexture from '../../assets/vfx-aurora-reflection.webp';
import frostTexture from '../../assets/vfx-frost-frame-alpha.webp';
import particleAtlas from '../../assets/vfx-particle-atlas-alpha.webp';
import powderTexture from '../../assets/vfx-powder-plume.webp';
import type { RunTier } from '../../utils/runTiers';

interface WeatherParticle {
  x: number;
  y: number;
  depth: number;
  size: number;
  speed: number;
  drift: number;
  phase: number;
  sprite: number;
  rotation: number;
}

const PARTICLE_COUNTS = [16, 32, 60, 104, 180, 18] as const;
const ATLAS_CELLS = 4;
const SNOW_SPRITES = [0, 2, 5, 6, 8, 10, 11, 12, 13] as const;
const LIGHT_SNOW_SPRITES = [0, 2, 3, 4, 5, 6, 8, 10, 11, 12, 13] as const;
const STORM_RANGES: ReadonlyArray<readonly [number, number]> = [
  [.08, .16], [.18, .31], [.34, .54], [.58, .79], [.83, 1], [.08, .08],
];
const clamp01 = (value: number) => Math.max(0, Math.min(1, value));
const hash01 = (value: number) => {
  const x = Math.sin(value * 91.713 + 17.17) * 43758.5453;
  return x - Math.floor(x);
};

export const getOneWayGustState = (elapsedMs: number, periodMs: number, durationMs: number, offsetMs = 0) => {
  const shifted = Math.max(0, elapsedMs + offsetMs);
  const cycle = Math.floor(shifted / periodMs);
  const localMs = shifted - cycle * periodMs;
  const progress = clamp01(localMs / durationMs);
  const active = localMs < durationMs;
  return {
    active,
    cycle,
    progress,
    envelope: active ? Math.pow(Math.sin(Math.PI * progress), 1.35) : 0,
  };
};

/** Additional Aurora intensity after the initial 25x breakthrough. */
export const getAuroraEscalation = (multiplier: number) => {
  if (multiplier < 25) return 0;
  if (multiplier < 40) return ((multiplier - 25) / 15) * .32;
  if (multiplier < 75) return .32 + ((multiplier - 40) / 35) * .36;
  if (multiplier < 100) return .68 + ((multiplier - 75) / 25) * .32;
  return 1;
};

const loadTexture = (src: string) => {
  const image = new Image();
  image.decoding = 'async';
  image.src = src;
  return image;
};

const textures = {
  aurora: loadTexture(auroraTexture),
  auroraArc: loadTexture(auroraArcTexture),
  auroraReflection: loadTexture(auroraReflectionTexture),
  frost: loadTexture(frostTexture),
  particles: loadTexture(particleAtlas),
  powder: loadTexture(powderTexture),
};

/** Tier-aware, asset-driven weather composited around the racers. */
export class RunWeatherEffect {
  private width: number;
  private height: number;
  private elapsedMs = 0;
  private tierElapsedMs = 0;
  private tierIndex = 0;
  private tierProgress = 0;
  private multiplier = 1;
  private stormIntensity = .08;
  private gustStrength = 0;
  private milestonePulse = 0;
  private milestoneMultiplier = 0;
  private previousMultiplier = 1;
  private active = false;
  private particles: WeatherParticle[] = [];

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.createParticles();
  }

  resize(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.createParticles();
  }

  reset() {
    this.elapsedMs = 0;
    this.tierElapsedMs = 0;
    this.tierIndex = 0;
    this.tierProgress = 0;
    this.multiplier = 1;
    this.stormIntensity = .08;
    this.gustStrength = 0;
    this.milestonePulse = 0;
    this.milestoneMultiplier = 0;
    this.previousMultiplier = 1;
    this.active = false;
  }

  update(dtMs: number, tier: RunTier, active: boolean, tierProgress = 0, multiplier = 1) {
    this.active = active;
    if (tier.index !== this.tierIndex) {
      this.tierElapsedMs = 0;
    }
    this.tierIndex = tier.index;
    this.tierProgress = clamp01(tierProgress);
    this.multiplier = Math.max(1, Number.isFinite(multiplier) ? multiplier : 1);
    if (tier.id === 'aurora') {
      const crossed = [25, 40, 75, 100].filter(value => this.previousMultiplier < value && this.multiplier >= value).pop();
      if (crossed) {
        this.milestonePulse = 1;
        this.milestoneMultiplier = crossed;
      }
    }
    this.previousMultiplier = this.multiplier;
    if (!active) return;
    const clampedMs = Math.max(0, Math.min(dtMs, 100));
    const dt = clampedMs / 16.667;
    this.elapsedMs += clampedMs;
    this.tierElapsedMs += clampedMs;
    this.milestonePulse = Math.max(0, this.milestonePulse - clampedMs / 1250);

    const [minimum, maximum] = STORM_RANGES[tier.index] ?? STORM_RANGES[0];
    const targetIntensity = minimum + (maximum - minimum) * this.tierProgress;
    const smoothing = clampedMs === 0 ? 1 : 1 - Math.exp(-clampedMs / 720);
    this.stormIntensity += (targetIntensity - this.stormIntensity) * smoothing;

    const t = this.elapsedMs / 1000;
    if (tier.id === 'aurora') {
      this.gustStrength += (0 - this.gustStrength) * (clampedMs === 0 ? 1 : 1 - Math.exp(-clampedMs / 420));
    } else {
      const slowWave = (Math.sin(t * .82 + Math.sin(t * .19) * 2.2) + 1) / 2;
      const gustPulse = Math.pow(clamp01((slowWave - .32) / .68), 2.35);
      const turbulence = (Math.sin(t * 2.17 + 1.4) + 1) * .055;
      this.gustStrength = clamp01((.055 + this.stormIntensity * .15) + (gustPulse * .56 + turbulence) * this.stormIntensity);
    }

    const stormStrength = tier.id === 'aurora'
      ? .17
      : .42 + this.stormIntensity * 1.72 + this.gustStrength * 1.18;
    const lateralWind = tier.id === 'aurora'
      ? .14
      : .48 + this.stormIntensity * 1.35 + this.gustStrength * 2.5 + Math.sin(t * .13) * .3;
    const count = this.getVisibleParticleCount();
    for (let index = 0; index < count; index++) {
      const particle = this.particles[index];
      particle.y += particle.speed * stormStrength * dt;
      particle.x -= (particle.drift * stormStrength + lateralWind * (.45 + particle.depth) + Math.sin(t * 1.5 + particle.phase) * .32) * dt;
      particle.rotation += (.002 + particle.depth * .004 + this.gustStrength * .006) * dt;
      if (particle.y > this.height + 30) particle.y = -30 - hash01(index + this.elapsedMs) * 70;
      if (particle.x < -42) particle.x = this.width + 42;
      if (particle.x > this.width + 42) particle.x = -42;
    }
  }

  getVisibleParticleCount() { return PARTICLE_COUNTS[this.tierIndex] ?? PARTICLE_COUNTS[0]; }
  getTierIndex() { return this.tierIndex; }
  getStormIntensity() { return this.stormIntensity; }
  getGustStrength() { return this.gustStrength; }

  drawBack(ctx: CanvasRenderingContext2D, tier: RunTier) {
    if (!this.active) return;
    this.drawBackgroundGrade(ctx, tier);
    if (tier.id === 'aurora') this.drawAurora(ctx);
    if (tier.index >= 1 && tier.id !== 'aurora') this.drawPowder(ctx, tier, false);
    this.drawParticles(ctx, tier, false);
    if (tier.id === 'aurora') this.drawAuroraMotes(ctx, false);
  }

  drawFront(ctx: CanvasRenderingContext2D, tier: RunTier) {
    if (!this.active) return;
    this.drawForegroundGrade(ctx, tier);
    this.drawParticles(ctx, tier, true);
    if (tier.id === 'aurora') this.drawAuroraMotes(ctx, true);
    if (tier.index >= 1 && tier.id !== 'aurora') this.drawPowder(ctx, tier, true);
    if (tier.index >= 3 && tier.id !== 'aurora') this.drawVisibilitySheets(ctx);
    if (tier.index >= 1 && tier.id !== 'aurora') this.drawFrost(ctx);
    if (tier.id === 'aurora') this.drawAuroraBloom(ctx);
    if (tier.id === 'aurora') this.drawMilestoneBurst(ctx);
  }

  private createParticles() {
    this.particles = Array.from({ length: PARTICLE_COUNTS[4] }, (_, index) => ({
      x: hash01(index * 7 + 1) * this.width,
      y: hash01(index * 11 + 2) * this.height,
      depth: hash01(index * 13 + 3),
      size: .65 + hash01(index * 17 + 4) * 2.7,
      speed: 1.05 + hash01(index * 19 + 5) * 4.5,
      drift: .4 + hash01(index * 23 + 6) * 2.3,
      phase: hash01(index * 29 + 7) * Math.PI * 2,
      sprite: Math.floor(hash01(index * 31 + 8) * 16),
      rotation: hash01(index * 37 + 9) * Math.PI * 2,
    }));
  }

  private drawBackgroundGrade(ctx: CanvasRenderingContext2D, tier: RunTier) {
    const auroraReveal = this.getAuroraReveal();
    const alpha = tier.id === 'aurora'
      ? .18 + auroraReveal * .18
      : .025 + this.stormIntensity * .12 + this.gustStrength * .018;
    ctx.save();
    if (tier.id === 'aurora') {
      ctx.fillStyle = `rgba(2,10,30,${alpha})`;
      ctx.fillRect(0, 0, this.width, this.height);
      const escalation = getAuroraEscalation(this.multiplier);
      const vignette = ctx.createRadialGradient(this.width * .5, this.height * .45, this.width * .16, this.width * .5, this.height * .45, this.width * .72);
      vignette.addColorStop(0, 'rgba(5,12,31,0)');
      vignette.addColorStop(.53, `rgba(4,11,34,${.055 + escalation * .03})`);
      vignette.addColorStop(1, `rgba(2,5,22,${.34 + escalation * .12})`);
      ctx.fillStyle = vignette;
      ctx.fillRect(0, 0, this.width, this.height);
    } else {
      ctx.fillStyle = `rgba(22,67,86,${alpha})`;
      ctx.fillRect(0, 0, this.width, this.height);
    }
    ctx.restore();
  }

  private drawForegroundGrade(ctx: CanvasRenderingContext2D, tier: RunTier) {
    const alpha = tier.id === 'aurora'
      ? .055 + this.getAuroraReveal() * .04
      : .006 + this.stormIntensity * .044;
    ctx.save();
    ctx.fillStyle = tier.id === 'aurora'
      ? `rgba(5,18,42,${alpha})`
      : `rgba(27,72,91,${alpha})`;
    ctx.fillRect(0, 0, this.width, this.height);
    ctx.restore();
  }

  private drawParticles(ctx: CanvasRenderingContext2D, tier: RunTier, foreground: boolean) {
    const image = textures.particles;
    if (!image.complete || image.naturalWidth === 0) return;
    const count = this.getVisibleParticleCount();
    const cellWidth = image.naturalWidth / ATLAS_CELLS;
    const cellHeight = image.naturalHeight / ATLAS_CELLS;
    const tierScale = tier.id === 'aurora' ? .7 : .8 + this.stormIntensity * .4 + this.gustStrength * .12;
    const baseAlpha = tier.id === 'aurora'
      ? .05 + (1 - this.getAuroraReveal()) * .11
      : .17 + this.stormIntensity * .44 + this.gustStrength * .15;

    ctx.save();
    ctx.globalCompositeOperation = 'source-over';
    for (let index = 0; index < count; index++) {
      const particle = this.particles[index];
      if ((particle.depth >= .54) !== foreground) continue;
      const spritePool = tier.index >= 3 && tier.id !== 'aurora' ? SNOW_SPRITES : LIGHT_SNOW_SPRITES;
      const sprite = spritePool[(particle.sprite + index) % spritePool.length];
      const sourceX = (sprite % ATLAS_CELLS) * cellWidth;
      const sourceY = Math.floor(sprite / ATLAS_CELLS) * cellHeight;
      const depthScale = .58 + particle.depth * .74;
      const displaySize = (5 + particle.size * (foreground ? 8 : 5.5)) * depthScale * tierScale;
      ctx.save();
      ctx.translate(particle.x, particle.y);
      ctx.rotate(particle.rotation);
      ctx.globalAlpha = Math.min(.78, baseAlpha * depthScale);
      if (foreground && this.stormIntensity > .68 && this.gustStrength > .25 && index % 2 === 0) {
        const echo = displaySize * (1.05 + this.gustStrength * .38);
        ctx.globalAlpha *= .24 * this.gustStrength;
        ctx.drawImage(image, sourceX, sourceY, cellWidth, cellHeight, displaySize * .45, -displaySize * 1.25, echo, echo * 1.3);
        ctx.globalAlpha = Math.min(.78, baseAlpha * depthScale);
      }
      ctx.drawImage(image, sourceX, sourceY, cellWidth, cellHeight, -displaySize / 2, -displaySize / 2, displaySize, displaySize);
      ctx.restore();
    }
    ctx.restore();
  }

  private drawPowder(ctx: CanvasRenderingContext2D, tier: RunTier, foreground: boolean) {
    const image = textures.powder;
    if (!image.complete || image.naturalWidth === 0) return;
    const strength = this.stormIntensity;
    const layers = strength > .66 ? 3 : strength > .4 ? 2 : 1;

    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    for (let layer = 0; layer < layers; layer++) {
      const period = 4100 + layer * 1330 + (foreground ? 730 : 0);
      const duration = 1550 + layer * 260 + strength * 360;
      const gust = getOneWayGustState(this.elapsedMs, period, duration, layer * 1510 + (foreground ? 890 : 0));
      if (!gust.active || gust.envelope < .01) continue;
      const variation = hash01(gust.cycle * 19 + layer * 31 + (foreground ? 7 : 2));
      const width = this.width * (foreground ? .78 : .63) * (1 + layer * .08 + variation * .12);
      const height = width * image.naturalHeight / image.naturalWidth;
      const easedProgress = gust.progress * gust.progress * (3 - 2 * gust.progress);
      const x = this.width + width * .08 - easedProgress * (this.width + width * 1.16);
      const yRatio = .08 + layer * .27 + (variation - .5) * .15;
      const y = this.height * yRatio - height * .25;
      const layerGate = layer === 0 ? 1 : layer === 1 ? clamp01((strength - .35) / .2) : clamp01((strength - .61) / .2);
      ctx.globalAlpha = gust.envelope * layerGate * strength * (foreground ? .3 : .19) * (1 - layer * .1) * (.88 + this.gustStrength * .5);
      if (variation > .52) {
        ctx.save();
        ctx.translate(x + width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(image, 0, y, width, height);
        ctx.restore();
      } else {
        ctx.drawImage(image, x, y, width, height);
      }
    }
    ctx.restore();
  }

  private drawFrost(ctx: CanvasRenderingContext2D) {
    const image = textures.frost;
    if (!image.complete || image.naturalWidth === 0) return;
    const t = this.elapsedMs / 1000;
    const frostLevel = clamp01((this.stormIntensity - .12) / .88);
    const breathe = .82 + Math.sin(t * .73) * .09 + Math.sin(t * .24 + 1.8) * .065 + this.gustStrength * .2;
    const minDimension = Math.min(this.width, this.height);
    const insets = [0, minDimension * .026];
    ctx.save();
    ctx.globalCompositeOperation = 'source-over';
    for (let layer = 0; layer < 2; layer++) {
      const threshold = layer === 0 ? 0 : .34;
      const presence = clamp01((frostLevel - threshold) / .24);
      if (presence <= 0) continue;
      const inset = insets[layer];
      const driftX = Math.sin(t * (.18 + layer * .07) + layer) * (1.5 + layer);
      const driftY = Math.cos(t * (.15 + layer * .05) + layer * 1.7) * (1 + layer);
      ctx.globalAlpha = presence * (.05 + frostLevel * .3) * breathe * (1 - layer * .2);
      if (layer === 0) {
        ctx.drawImage(image, inset + driftX, inset + driftY, this.width - inset * 2, this.height - inset * 2);
      } else {
        ctx.save();
        ctx.translate(this.width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(image, inset - driftX, inset + driftY, this.width - inset * 2, this.height - inset * 2);
        ctx.restore();
      }
    }

    const hazeStrength = clamp01((frostLevel - .5) / .5) * (.07 + this.gustStrength * .08);
    if (hazeStrength > .005) {
      const haze = ctx.createRadialGradient(this.width * .5, this.height * .47, this.width * .2, this.width * .5, this.height * .47, this.width * .68);
      haze.addColorStop(0, 'rgba(211,239,248,0)');
      haze.addColorStop(.65, `rgba(176,216,231,${hazeStrength * .18})`);
      haze.addColorStop(1, `rgba(133,187,207,${hazeStrength})`);
      ctx.fillStyle = haze;
      ctx.fillRect(0, 0, this.width, this.height);
    }
    ctx.restore();
  }

  private drawVisibilitySheets(ctx: CanvasRenderingContext2D) {
    const image = textures.powder;
    if (!image.complete || image.naturalWidth === 0) return;
    const presence = clamp01((this.stormIntensity - .52) / .48);
    const gust = getOneWayGustState(this.elapsedMs, 5900, 2450, 1740);
    const gustVisibility = gust.envelope * presence * (.08 + this.gustStrength * .3);
    if (gustVisibility < .012) return;
    const variation = hash01(gust.cycle * 41 + 5);
    const width = this.width * (1.24 + variation * .22);
    const height = width * image.naturalHeight / image.naturalWidth;
    const easedProgress = gust.progress * gust.progress * (3 - 2 * gust.progress);
    const x = this.width + width * .06 - easedProgress * (this.width + width * 1.12);
    const y = -height * (.1 + variation * .18) + this.height * (variation - .5) * .1;
    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    ctx.globalAlpha = gustVisibility;
    ctx.drawImage(image, x, y, width, height);
    ctx.restore();

    const veil = ctx.createRadialGradient(this.width * .5, this.height * .46, this.width * .12, this.width * .5, this.height * .46, this.width * .68);
    veil.addColorStop(0, 'rgba(225,244,250,0)');
    veil.addColorStop(.66, `rgba(205,231,240,${gustVisibility * .16})`);
    veil.addColorStop(1, `rgba(184,216,229,${gustVisibility * .52})`);
    ctx.save();
    ctx.fillStyle = veil;
    ctx.fillRect(0, 0, this.width, this.height);
    ctx.restore();
  }

  private drawAurora(ctx: CanvasRenderingContext2D) {
    const curtain = textures.aurora;
    const arc = textures.auroraArc;
    if (!curtain.complete || curtain.naturalWidth === 0 || !arc.complete || arc.naturalWidth === 0) return;
    const t = this.elapsedMs / 1000;
    const reveal = this.getAuroraReveal();
    if (reveal <= 0) return;

    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    this.drawCelestialOpening(ctx, reveal);
    const escalation = getAuroraEscalation(this.multiplier);
    ctx.globalAlpha = reveal * (.42 + escalation * .07 + Math.sin(t * .43) * .045);
    ctx.drawImage(curtain, -this.width * .06 + Math.sin(t * .15) * this.width * .025, -this.height * .08, this.width * 1.12, this.height * 1.06);

    ctx.save();
    ctx.translate(this.width, 0);
    ctx.scale(-1, 1);
    ctx.globalAlpha = reveal * (.16 + escalation * .035 + Math.sin(t * .31 + 1.7) * .025);
    ctx.drawImage(curtain, -this.width * .1 + Math.sin(t * .11 + 1.2) * this.width * .018, -this.height * .12, this.width * 1.18, this.height * 1.02);
    ctx.restore();

    const cycleSeconds = 13.5;
    for (let pass = 0; pass < 2; pass++) {
      const phase = ((t / cycleSeconds + pass * .5) % 1 + 1) % 1;
      const envelope = Math.pow(Math.sin(Math.PI * phase), 1.18);
      const arcWidth = this.width * 1.05;
      const arcHeight = arcWidth * arc.naturalHeight / arc.naturalWidth;
      const x = -this.width * .14 + phase * this.width * .22;
      const y = -arcHeight * .4 - pass * this.height * .018;
      ctx.globalAlpha = reveal * envelope * (pass === 0 ? .2 : .12) * (1 + escalation * .1);
      ctx.drawImage(arc, x, y, arcWidth, arcHeight);
    }
    ctx.restore();
  }

  private drawAuroraBloom(ctx: CanvasRenderingContext2D) {
    const t = this.elapsedMs / 1000;
    const reveal = this.getAuroraReveal();
    const escalation = getAuroraEscalation(this.multiplier);
    const reflectionImage = textures.auroraReflection;
    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    const bloom = ctx.createRadialGradient(this.width * .5, this.height * .32, 0, this.width * .5, this.height * .32, this.width * .48);
    bloom.addColorStop(0, `rgba(255,246,207,${reveal * (.1 + escalation * .055 + Math.sin(t * .62) * .02)})`);
    bloom.addColorStop(.38, `rgba(125,244,233,${reveal * (.075 + escalation * .04)})`);
    bloom.addColorStop(.68, `rgba(84,165,225,${reveal * .035})`);
    bloom.addColorStop(1, 'rgba(90,85,190,0)');
    ctx.fillStyle = bloom;
    ctx.fillRect(0, 0, this.width, this.height);

    if (reflectionImage.complete && reflectionImage.naturalWidth > 0) {
      const cycleSeconds = 13.5;
      for (let pass = 0; pass < 2; pass++) {
        const phase = ((t / cycleSeconds + pass * .5) % 1 + 1) % 1;
        const envelope = Math.pow(Math.sin(Math.PI * phase), 1.25);
        const reflectionWidth = this.width * 1.28;
        const reflectionHeight = reflectionWidth * reflectionImage.naturalHeight / reflectionImage.naturalWidth;
        const x = -this.width * .24 + phase * this.width * .28;
        const y = this.height * .25;
        ctx.globalAlpha = reveal * envelope * (pass === 0 ? .3 : .2) * (1 + escalation * .28);
        ctx.drawImage(reflectionImage, x, y, reflectionWidth, reflectionHeight);
      }
    }

    const breakthrough = this.getBreakthroughPulse();
    if (breakthrough > .001) {
      const release = ctx.createRadialGradient(this.width * .5, this.height * .42, 0, this.width * .5, this.height * .42, this.width * .62);
      release.addColorStop(0, `rgba(255,255,245,${breakthrough * .42})`);
      release.addColorStop(.34, `rgba(203,250,255,${breakthrough * .2})`);
      release.addColorStop(1, 'rgba(180,230,255,0)');
      ctx.fillStyle = release;
      ctx.fillRect(0, 0, this.width, this.height);
    }
    ctx.restore();
  }

  private drawMilestoneBurst(ctx: CanvasRenderingContext2D) {
    if (this.milestonePulse <= .001) return;
    const progress = 1 - this.milestonePulse;
    const envelope = Math.sin(Math.PI * progress);
    const x = this.width * .5;
    const y = this.height * .755;
    const importance = this.milestoneMultiplier >= 100 ? 1 : this.milestoneMultiplier >= 75 ? .8 : this.milestoneMultiplier >= 40 ? .62 : .48;
    const radius = 20 + progress * this.width * (.13 + importance * .035);
    const colour = this.milestoneMultiplier >= 75 ? '255,224,155' : '153,255,237';
    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    ctx.globalAlpha = envelope * (.48 + importance * .22);
    ctx.strokeStyle = `rgba(${colour},${.78 - progress * .52})`;
    ctx.lineWidth = 2.4 - progress * 1.2;
    ctx.beginPath();ctx.ellipse(x, y, radius * 1.7, radius * .58, 0, 0, Math.PI * 2);ctx.stroke();
    const burstCount = 12 + Math.round(importance * 10);
    for (let index = 0; index < burstCount; index++) {
      const angle = hash01(index * 149 + this.milestoneMultiplier) * Math.PI * 2;
      const distance = radius * (.45 + hash01(index * 151 + 7) * .76);
      const px = x + Math.cos(angle) * distance * 1.45;
      const py = y + Math.sin(angle) * distance * .62;
      const size = 1 + hash01(index * 157 + 9) * 2.2;
      ctx.fillStyle = index % 3 === 0 ? `rgba(255,237,182,${envelope * .75})` : `rgba(178,255,241,${envelope * .65})`;
      ctx.save();ctx.translate(px,py);ctx.rotate(Math.PI/4);ctx.fillRect(-size/2,-size/2,size,size);ctx.restore();
    }
    ctx.restore();
  }

  private drawCelestialOpening(ctx: CanvasRenderingContext2D, reveal: number) {
    const t = this.elapsedMs / 1000;
    const escalation = getAuroraEscalation(this.multiplier);
    const opening = ctx.createRadialGradient(this.width * .5, this.height * .4, 0, this.width * .5, this.height * .4, this.width * .52);
    opening.addColorStop(0, `rgba(255,250,218,${reveal * (.13 + escalation * .07)})`);
    opening.addColorStop(.28, `rgba(218,255,250,${reveal * (.11 + escalation * .045)})`);
    opening.addColorStop(.72, `rgba(104,214,229,${reveal * .025})`);
    opening.addColorStop(1, 'rgba(82,154,220,0)');
    ctx.fillStyle = opening;
    ctx.fillRect(0, 0, this.width, this.height);

    const rayCount = 5 + Math.round(escalation * 3);
    for (let index = 0; index < rayCount; index++) {
      const seed = hash01(index * 47 + 11);
      const sway = Math.sin(t * (.09 + index * .008) + seed * 6.28) * this.width * .035;
      const centre = this.width * (.16 + seed * .68) + sway;
      const topWidth = this.width * (.012 + hash01(index * 61 + 3) * .018);
      const bottomWidth = this.width * (.07 + hash01(index * 71 + 4) * .1);
      const rayEnd = this.height * (.57 + hash01(index * 79 + 8) * .18);
      const ray = ctx.createLinearGradient(centre - bottomWidth, 0, centre + bottomWidth, 0);
      ray.addColorStop(0, 'rgba(140,236,236,0)');
      ray.addColorStop(.44, `rgba(211,255,244,${reveal * (.025 + escalation * .018)})`);
      ray.addColorStop(.52, `rgba(255,240,194,${reveal * (.045 + escalation * .025)})`);
      ray.addColorStop(1, 'rgba(143,173,255,0)');
      ctx.fillStyle = ray;
      ctx.beginPath();
      ctx.moveTo(centre - topWidth, -10);
      ctx.lineTo(centre + topWidth, -10);
      ctx.lineTo(centre + bottomWidth, rayEnd);
      ctx.lineTo(centre - bottomWidth, rayEnd);
      ctx.closePath();
      ctx.fill();
    }
  }

  private drawAuroraMotes(ctx: CanvasRenderingContext2D, foreground: boolean) {
    const reveal = this.getAuroraReveal();
    if (reveal <= 0) return;
    const escalation = getAuroraEscalation(this.multiplier);
    const count = (foreground ? 10 : 15) + Math.round(escalation * (foreground ? 8 : 12));
    const t = this.elapsedMs / 1000;
    const colours = ['183,255,238', '255,226,155', '210,187,255', '150,224,255'];
    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    for (let index = 0; index < count; index++) {
      const seed = hash01(index * 83 + (foreground ? 29 : 13));
      const duration = 5.2 + hash01(index * 97 + 4) * 4.8;
      const phase = ((t / duration + seed) % 1 + 1) % 1;
      const x = hash01(index * 101 + 7) * this.width + Math.sin(t * .42 + seed * 8) * (8 + escalation * 12);
      const y = this.height * (1.04 - phase * 1.12);
      const envelope = Math.sin(Math.PI * phase);
      const size = (foreground ? 1.8 : .85) + hash01(index * 107 + 5) * (foreground ? 2.8 : 1.6);
      const alpha = reveal * envelope * (.15 + escalation * .14) * (foreground ? .88 : .62);
      const colour = colours[index % colours.length];
      ctx.fillStyle = `rgba(${colour},${alpha})`;
      ctx.shadowColor = `rgba(${colour},${alpha * .9})`;
      ctx.shadowBlur = size * (3 + escalation * 2);
      if (index % 4 === 0 && foreground) {
        ctx.beginPath();
        ctx.arc(x, y, size * 1.45, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(Math.PI / 4 + Math.sin(t * .35 + seed) * .14);
        ctx.fillRect(-size / 2, -size / 2, size, size);
        ctx.restore();
      }
    }
    ctx.restore();
  }

  private getBreakthroughPulse() {
    if (this.tierIndex !== 5) return 0;
    const progress = clamp01((this.tierElapsedMs - 80) / 1150);
    return Math.pow(Math.sin(Math.PI * progress), 1.15);
  }

  private getAuroraReveal() {
    if (this.tierIndex !== 5) return 0;
    const delayed = clamp01((this.tierElapsedMs - 180) / 1650);
    return delayed * delayed * (3 - 2 * delayed);
  }
}
